<template>
  <div v-if="!hasForgoPayments" class="stripe-payment-card">
    <div ref="card" v-show="!showSavedCard" id="card-element"></div>
    <Loader :fixed="false" :hideLoadingText="true" v-if="!showSavedCard && isStripeInitializing" />
    <SavedPaymentMethod v-if="showSavedCard" />
    <div v-if="!hideUpdateCard && showSavedCard" @click="showStripCardField" class="update-card">
      {{ $t("stripPaymentCard.updateCard") }}
    </div>
    <span class="error" v-html="cardValidationError" v-if="cardValidationError" />
    <p
      v-if="!skipPaymentCharge"
      v-html="helperText"
      :class="['helper-text', showCheckbox && 'amountText']"
    />
    <div class="checkbox" v-if="showCheckbox">
      <p v-if="helperSubText" v-html="helperSubText" class="sub-helper-text" />
      <input
        type="checkbox"
        id="checkboxContent"
        name="checkboxContent"
        value="checkboxContent"
        @change="toggleChecked"
        checked
        v-if="showAutoRenew"
      />
      <label v-if="showAutoRenew" for="checkboxContent">{{ checkboxText }}</label>
    </div>
    <button
      :disabled="showButtonSpinner"
      :class="{
        'show-spinner': showButtonSpinner,
        'mt-16': skipPaymentCharge,
      }"
      @click="onPayNowClicked()"
      v-html="
        buttonText ||
        $t(skipPaymentCharge ? 'stripPaymentCard.payZero' : 'stripPaymentCard.payNow', {
          currencyCode,
        })
      "
    />
  </div>
  <div v-else class="stripe-payment-card">
    <div v-if="hasForgoPayments" class="forgo-payments-info">
      <div>
        {{ $t("billingAndSubscription.paymentMethodWithPartner", [partnerCompanyName]) }}
      </div>
    </div>
    <p v-html="forgoModeHelperText" class="helper-text" />
    <button
      :disabled="showButtonSpinner"
      :class="{ 'show-spinner': showButtonSpinner }"
      @click="onPayNowClicked()"
    >
      {{ buttonText || $t("stripPaymentCard.payNow") }}
    </button>
  </div>
</template>

<script>
import CountryNotSupportedModal from "@/components/CountryNotSupportedModal/CountryNotSupportedModal.vue";
import Loader from "@/components/Utility/Loader.vue";
import { COMMON_MODAL_EMITTERS } from "@/helpers/const";
import eventBus from "@/helpers/event-bus";
import { Stripe } from "@/helpers/stripe";
import { mapActions, mapGetters } from "vuex";
import {
  getCommonMedusaParamsForUpgradePlan,
  getProductToPurchaseAttr,
  triggerPurchaseAndBillingEvents,
} from "@/telemetry/medusaUtils";
import {
  getAppSource,
  getCurrencySymbol,
  getCurrentUserEmail,
  getDomainName,
  isNeoBrand,
  returnObjectIfValueExists,
} from "@/helpers";
import SavedPaymentMethod from "@/components/Utility/SavedPaymentMethod.vue";
import billingService from "@/services/billing/billing.service";
import Logger from "@/helpers/logger";
import bundleEvents from "@/telemetry/bundleEvents";
import { hasMonthExtensionActionParam } from "@/helpers/actionHelpers";
import { removeDomainNamePropsFromObj } from "@/helpers/domainHelpers";
import { MEDUSA_EVENTS_MAP } from "@/telemetry/events";
import {
  neoPaymentElementConfig,
  paymentElementAppearance,
  titanPaymentElementConfig,
} from "@/const/stripe.constant";

export default {
  name: "StripePaymentCard",
  components: {
    SavedPaymentMethod,
    Loader,
  },
  data() {
    return {
      checked: true,
      showButtonSpinner: false,
      elements: null,
      isStripeInitializing: false,
      cardValidationError: null,
      stripePaymentElement: null,
      domain: this.domainProp || getDomainName(),
      loginEmail: this.loginEmailProp || getCurrentUserEmail(),
      payment_transaction_id: null,
      isFirstTimePaymentMethodSelected: true,
      selectedPaymentMethod: "",
    };
  },
  props: {
    domainProp: { type: String },
    loginEmailProp: { type: String },
    isTrailPlan: {
      type: Boolean,
      default: false, // in existing flow this cmp used for purchase
    },
    amountToBePaid: {
      type: Number,
    },
    billingCycle: {
      type: String,
    },
    showAutoRenew: {
      type: Boolean,
      default: true,
    },
    hideUpdateCard: {
      type: Boolean,
    },
    skipPaymentCharge: {
      type: Boolean,
    },
    isProvisionFlow: {
      type: Boolean,
    },
    setAmountIncTax: {
      type: Function,
    },
    authorizationHeader: {
      type: String,
    },
    processPaymentAPI: {
      type: String,
    },
    processPaymentProps: {
      type: Object,
      required: false,
    },
    isBundlePaymentFlow: {
      type: Boolean,
      default: false,
    },
    initPaymentAPI: {
      type: String,
    },
    showCheckbox: {
      type: Boolean,
    },
    checkboxText: {
      type: String,
    },
    helperText: {
      type: String,
      default: "",
    },
    forgoModeHelperText: {
      type: String,
      default: "",
    },
    helperSubText: {
      type: String,
      default: "",
    },
    buttonText: {
      type: String,
      default: "",
    },
    paymentPayload: {
      type: Object,
      default: {},
    },
    currency: {
      type: String,
      default: "",
    },
    shouldStartPayment: {
      type: Function,
    },
    paymentEventsMedusaAttributes: {
      type: Object,
      default: () => {},
    },
    paymentFor: {
      type: String,
    },
    getMedusaAttr: {
      type: Function,
    },
    sourceHook: {
      type: String,
    },
    selectedRecord: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    newMailboxCount: {
      type: Number,
      require: false,
      default: () => -1,
    },
  },
  computed: {
    ...mapGetters(["locale", "partnerInfo", "isBlacklistedCountry"]),
    ...mapGetters({
      paymentCardType: "payment/paymentCardType",
      savedCard: "payment/savedCard",
      bundleId: "bundle/bundleId",
    }),
    /**
     * This flow's session token arrives as a prop and never reaches storage, so medusa is given it
     * explicitly — the same way every API call here passes it via `$root.headers`.
     */
    medusaOptions() {
      return { token: this.authorizationHeader };
    },
    showSavedCard() {
      return !!this.savedCard?.paymentMethodId;
    },
    hasForgoPayments() {
      return this.partnerInfo?.flags?.forgoPayments;
    },
    partnerCompanyName() {
      return this.partnerInfo?.companyName;
    },
    currencyCode() {
      return getCurrencySymbol(this.currency);
    },
  },
  methods: {
    ...mapActions({
      setCardType: "payment/paymentCardType",
    }),
    toggleChecked() {
      this.$emit("toggleChecked");
    },
    showStripCardField() {
      this.$emit("openManageCardModal", "update", this.getMedusaAttr?.());
    },
    handleError(err) {
      this.showButtonSpinner = false;
      this.$emit("onPaymentFailed", err);
      console.log("transaction failed", err);
      const medusaPayload = {
        ...this.paymentEventsMedusaAttributes,
        source: getAppSource(),
        source_hook: this.sourceHook,
        error: err?.message,
        // add payment_card_type and payment_transaction_id key to payload only if it's value exist
        ...returnObjectIfValueExists("payment_card_type", this.paymentCardType),
        ...returnObjectIfValueExists("payment_transaction_id", this.payment_transaction_id),
      };
      triggerPurchaseAndBillingEvents.call(
        this,
        {
          ...medusaPayload,
          ...(this.selectedRecord ? getCommonMedusaParamsForUpgradePlan(this.selectedRecord) : {}),
          eventName: MEDUSA_EVENTS_MAP.PAYMENT_FAILED,
          payment_method: this.showSavedCard
            ? this.savedCard?.walletType || "card"
            : this.selectedPaymentMethod,
        },
        false,
        this.medusaOptions
      );
      bundleEvents.logPaymentFailed(
        {
          ...medusaPayload,
          ...getProductToPurchaseAttr(this.selectedRecord),
          ...this.getBundleEventAttr(),
        },
        this.medusaOptions
      );
    },
    getBundleEventAttr() {
      return {
        payment_amount: this.amountToBePaid,
        new_billing_cycle: this.billingCycle,
        ...(this.newMailboxCount !== -1 ? { new_mailbox_count: this.newMailboxCount } : {}),
      };
    },
    triggerPurchaseSuccessMedusaEvents() {
      try {
        const medusaObject = {
          ...this.paymentEventsMedusaAttributes,
          source: getAppSource(),
          source_hook: this.sourceHook,
          payment_method: this.showSavedCard
            ? this.savedCard?.walletType || "card"
            : this.selectedPaymentMethod,
          // add payment_card_type and payment_transaction_id key to payload only if it's value exist
          ...returnObjectIfValueExists("payment_card_type", this.paymentCardType),
          ...returnObjectIfValueExists("payment_transaction_id", this.payment_transaction_id),
          ...returnObjectIfValueExists("free_month_eligible", hasMonthExtensionActionParam()),
          ...(this.getMedusaAttr?.() || {}),
        };

        // PURCHASE_COMPLETED
        triggerPurchaseAndBillingEvents.call(
          this,
          {
            ...medusaObject,
            ...(this.selectedRecord
              ? getCommonMedusaParamsForUpgradePlan(this.selectedRecord)
              : {}),
            eventName: MEDUSA_EVENTS_MAP.PURCHASE_COMPLETED,
          },
          true,
          this.medusaOptions
        );
        bundleEvents.logPurchaseComplete(
          {
            ...medusaObject,
            ...getProductToPurchaseAttr(this.selectedRecord),
            ...this.getBundleEventAttr(),
          },
          this.medusaOptions
        );

        // PAYMENT_COMPLETED
        triggerPurchaseAndBillingEvents.call(
          this,
          {
            ...medusaObject,
            eventName: MEDUSA_EVENTS_MAP.PAYMENT_COMPLETED,
          },
          true,
          this.medusaOptions
        );
        bundleEvents.logPaymentComplete(
          {
            ...medusaObject,
            ...getProductToPurchaseAttr(this.selectedRecord),
            ...this.getBundleEventAttr(),
          },
          this.medusaOptions
        );
      } catch (e) {
        Logger.error("`triggerPurchaseSuccessMedusaEvents` failed", e);
      }
    },
    handleSuccess(res) {
      this.showButtonSpinner = false;
      this.showAddonPurchaseModal = true;
      this.cardValidationError = null;
      if (!this.hasForgoPayments) {
        this.stripePaymentElement.clear();
      }
      this.triggerPurchaseSuccessMedusaEvents();
      this.$emit("onPaymentSuccess", res);
    },
    processPayment(completionToken) {
      const payload = this.isBundlePaymentFlow
        ? {
            billingTokenId: completionToken,
            bundleId: this.bundleId,
            ...(this.processPaymentProps || {}),
          }
        : {
            trackingId: completionToken,
            bundleId: this.bundleId,
            ...(this.processPaymentProps || {}),
          };

      this.http.postData(
        this.processPaymentAPI,
        payload,
        this.$root.headers,
        this.handleSuccess, // success
        this.handleError // error
      );
    },
    /**
     * function to decide whether to initiate stripe payment or add card flow
     * @param {string} intent - stripe intent
     */
    async addCardAndProcessPayment(intent) {
      // save payment method if not it not saved
      // then make payment
      const data = await this.confirmAddCardSetup();

      return this.confirmCardPayment(intent, data?.setupIntent?.payment_method);
    },
    /**
     * function to add card and initiate the payment
     * @param {string} intent stripe intent
     */
    confirmCardPayment(intent, paymentMethod) {
      return this.stripe.confirmCardPayment(intent, {
        payment_method: paymentMethod || this.savedCard.paymentMethodId,
      });
    },
    /**
     * function to add card and initiate the payment
     * @param {string} intent stripe intent
     */
    async confirmAddCardSetup() {
      if (this.showSavedCard) {
        // incase of trial plan, skip add card process if card was already added
        return Promise.resolve({ error: false });
      }

      try {
        const data = await this.stripe.confirmSetup({
          elements: this.elements,
          redirect: "if_required",
          confirmParams: {
            // this is required but not needed for card, apple/google pay
            return_url: window.location.href,
            payment_method_data: {
              billing_details: {
                email: this.loginEmail,
              },
            },
          },
        });

        let cardFunding = "unknown";
        if (data) {
          // sync vuex state with latest BLL data
          const latestCard = await billingService.getCardDetails();
          cardFunding = latestCard.funding || "unknown";
        }
        this.setCardType(cardFunding);
        // handle medusa entity change on server when the payment card gets changed
        this.medusaEvents.entityChange(
          {
            type: "titan_customer",
            attrs: {
              payment_card_type: cardFunding,
            },
          },
          this.medusaOptions
        );

        return data;
      } catch (e) {
        Logger.error("`confirmAddCardSetup` failed", e);
        return Promise.reject(e);
      }
    },
    async initPayment() {
      if (this.authorizationHeader) {
        this.$root.headers = {
          headers: { Authorization: this.authorizationHeader },
        };
      }

      // Remove domainName from paymentPayload before sending to API
      const payloadWithoutDomainName = removeDomainNamePropsFromObj(this.paymentPayload);

      return this.http.postData(
        this.initPaymentAPI,
        {
          ...payloadWithoutDomainName,
          bundleId: this.bundleId,
          currency: this.currency,
        },
        this.$root.headers,
        data => {
          // success
          if (data.chargeableAmount) {
            this.setAmountIncTax?.(data.chargeableAmount);
          }

          const completionToken = data.billingTokenId || data.trackingId || data.paymentTokenId;
          const paymentClientSecret = data.paymentClientSecret || data.stripeSecret;

          if (this.hasForgoPayments || this.skipPaymentCharge) {
            return this.processPayment(completionToken);
          }

          this.addCardAndProcessPayment(paymentClientSecret)
            .then(result => {
              if (result.error) {
                const {
                  error: { payment_intent },
                } = result;
                if (payment_intent) {
                  this.payment_transaction_id = payment_intent.id;
                }
                return Promise.reject(result.error);
              }

              const { paymentIntent } = result;
              if (paymentIntent) {
                this.payment_transaction_id = paymentIntent.id;
              }

              return this.processPayment(completionToken);
            })
            .catch(this.handleError);
        },
        this.handleError // error
      );
    },
    // checked whether there are any errors in stripe card element
    async hasValidationErrors() {
      if (this.showSavedCard) return false;

      const { error: submitError } = await this.elements.submit();
      const errorMessage = submitError?.message || submitError?.code;
      this.cardValidationError = errorMessage;
      return !!errorMessage;
    },
    async validateCardAndInitiatePayment() {
      try {
        const hasError = await this.hasValidationErrors();
        if (hasError) throw new Error("validation_error");

        this.showButtonSpinner = true;

        await this.initPayment();
      } catch (e) {
        Logger.error("`validateCardAndInitiatePayment` failed", e);
        return false;
      }
    },
    sendPayNowClickedEvt() {
      const medusaPayload = {
        payment_for: this.paymentFor,
        source_hook: this.sourceHook,
        payment_method: this.showSavedCard
          ? this.savedCard?.walletType || "card"
          : this.selectedPaymentMethod,
        ...returnObjectIfValueExists("free_month_eligible", hasMonthExtensionActionParam()),
        ...(this.getMedusaAttr?.() || {}),
      };
      triggerPurchaseAndBillingEvents.call(
        this,
        {
          eventName: MEDUSA_EVENTS_MAP.PAY_NOW_CLICKED,
          ...(this.selectedRecord ? getCommonMedusaParamsForUpgradePlan(this.selectedRecord) : {}),
          ...medusaPayload,
        },
        false,
        this.medusaOptions
      );
      bundleEvents.logPayNowClicked(
        {
          ...medusaPayload,
          ...getProductToPurchaseAttr(this.selectedRecord),
          ...this.getBundleEventAttr(),
        },
        this.medusaOptions
      );
    },
    async onPayNowClicked() {
      try {
        if (this.isBlacklistedCountry) {
          // show modal
          eventBus.$emit(COMMON_MODAL_EMITTERS.OPEN_MODAL, {
            component: CountryNotSupportedModal,
            modalWidth: "470px",
            replaceCurrentModal: true,
          });
          return;
        }
        if (this.showButtonSpinner) {
          // already transaction in in-progress
          return false;
        }

        // inform medusa about pay now clicked event
        this.sendPayNowClickedEvt();

        if (this.hasForgoPayments) {
          // incase of godaddy/ForgoPayments we don't need to validate card/make any payment
          return this.startForgoPayments();
        }

        if (this.shouldStartPayment) {
          // this is CB function to do any action before starting payment
          // based on the success/failure resp we decide to start payments
          await this.shouldStartPayment();
        }

        return this.validateCardAndInitiatePayment();
      } catch (e) {
        Logger.error("`onPayNowClicked` failed", e);
        return Promise.reject(e);
      }
    },
    startForgoPayments() {
      this.showButtonSpinner = true;
      this.initPayment();
    },
    async initStripePaymentElement() {
      try {
        // ignore if its GD forgo mode
        if (this.hasForgoPayments) return false;

        // start spinner
        this.isStripeInitializing = true;

        // initialize stripe instance
        this.stripe = await Stripe();
        const data = await billingService.getAddCardIntent();
        const clientSecret = data?.stripeSecret;

        if (!clientSecret) throw new Error("Missing Stripe client secret");

        /**
         * Send locale to stripe to get the translation for placeholder string of card element
         * Supported languages (https://stripe.com/docs/js/appendix/supported_locales)
         */
        this.elements = this.stripe.elements({
          locale: this.locale,
          clientSecret,
          paymentElementAppearance,
        });
        this.stripePaymentElement = this.elements.create(
          "payment",
          isNeoBrand() ? neoPaymentElementConfig : titanPaymentElementConfig
        );

        // mount stripe payment element to dom
        this.stripePaymentElement.mount(this.$refs.card);
        this.stripePaymentElement.on("change", this.onStripePaymentElementChange);
        this.stripePaymentElement.on("ready", this.onStripePaymentElementReady);
      } catch (e) {
        Logger.error("`initStripePaymentElement` failed", e);
        this.showToast(this.$t("billingAndUpgrade.manageCard.toastMessage.stripeInitFailure"));
      }
    },
    onStripePaymentElementChange(event) {
      if (event.value?.collapsed) return; // on load stripe call this method with collapsed true

      this.selectedPaymentMethod = event.value?.type || "unknown";
      triggerPurchaseAndBillingEvents.call(
        this,
        {
          eventName: MEDUSA_EVENTS_MAP.PAYMENT_METHOD_SELECTED,
          source: getAppSource(),
          source_hook: this.sourceHook,
          payment_method: this.selectedPaymentMethod,
          action: this.isFirstTimePaymentMethodSelected ? "selected" : "changed",
        },
        false,
        this.medusaOptions
      );
      this.isFirstTimePaymentMethodSelected = false;
    },
    onStripePaymentElementReady() {
      const isStripeInitialized = sessionStorage.getItem("stripeInitialized");
      if (isStripeInitialized) {
        this.isStripeInitializing = false;
        return;
      }

      // Optional: wait 2s to ensure Google/Apple Pay has had time to render
      setTimeout(() => {
        this.isStripeInitializing = false;
        sessionStorage.setItem("stripeInitialized", true);
      }, 2000);
    },
  },
  beforeDestroy() {
    this.stripePaymentElement?.off("change", this.onStripePaymentElementChange);
    this.stripePaymentElement?.off("ready", this.onStripePaymentElementReady);
  },
  mounted() {
    this.initStripePaymentElement();
  },
};
</script>

<style lang="scss">
.stripe-payment-card {
  /* margin: 24px 0 0; */

  .helper-text {
    font-size: 14px;
    margin-top: 16px;

    b {
      font-size: 16px !important;
    }
  }

  #card-element {
    width: 100%;
    max-width: 500px;
    border-radius: 4px;
    background-color: var(--tabBgCol);
  }

  .error {
    height: 15px;
    display: block;
    margin: 2px 0 4px;
  }
  .amountText {
    margin-bottom: 0;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--lineSepaCol);
  }
  .checkbox {
    // $params are in the given order
    // margin-top , margin-right, margin-bottom, margin-left
    // padding-top , padding-right, padding-bottom, padding-left
    // width, height
    @include custom-checkbox(17, 12, 39, 0, 2, 2, 2, 2, 16, 16);
  }

  .hide {
    display: none;
  }

  button {
    padding: 10px 18px;
    box-sizing: border-box;
    border-radius: 5px;
    background-color: var(--primaryBtnCol);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.43;
    text-align: center;
    color: var(--tabBgCol);
    outline: none !important;
    border: 1px solid var(--primaryBtnCol);

    &:active {
      border: 1px solid var(--primaryBtnCol);
    }

    &:disabled {
      cursor: not-allowed;
    }

    &.show-spinner {
      @include button-loader();
    }
  }

  .sub-helper-text {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 17px 0 29px;
  }

  .update-card {
    margin: 12px 0 0;
    font-weight: 500;
    text-decoration: underline;
    color: var(--linkBtnCol);
    cursor: pointer;
  }

  .forgo-payments-info {
    display: inline-block;
    padding: 9px 10px;
    border-radius: 4px;
    border: 1px solid var(--lineSepaCol);
    background: var(--forgoPaymentsInfoBg);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;

    & + p {
      margin: 22px 0 24px;
    }
  }

  .forgo-payments-info {
    display: inline-block;
    padding: 9px 10px;
    border-radius: 4px;
    border: 1px solid var(--lineSepaCol);
    background: var(--forgoPaymentsInfoBg);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;

    & + p {
      margin: 22px 0 24px;
    }
  }
}
</style>
